import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  PhotoIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  DocumentIcon,
  CpuChipIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  FaceSmileIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

/*const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';*/

const MemoryLogs = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: 'all',
    contentType: 'all',
    sentiment: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({});
  const [selectedLogs, setSelectedLogs] = useState([]);

  useEffect(() => {
    loadMemoryLogs();
    loadMemoryStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, searchQuery, filters]);

  const loadMemoryLogs = async () => {
    try {
      // Mock data - in real implementation, this would come from API
      const mockLogs = [
        {
          id: 1,
          type: 'chat',
          content: 'User asked about weather in New York',
          response: 'Provided current weather conditions and 5-day forecast',
          timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          contentType: 'conversation',
          sentiment: 'neutral',
          tokens: 150,
          processingTime: 1.2,
          agentUsed: 'WeatherAgent',
          session_id: 'sess_1'
        },
        {
          id: 2,
          type: 'media',
          content: 'Uploaded family vacation photos',
          response: 'Processed 12 images with OCR and captioning',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          contentType: 'image',
          sentiment: 'positive',
          mediaCount: 12,
          processingTime: 45.6,
          extractedText: 'Beach, family, summer 2024',
          captions: ['Family at beach', 'Sunset over ocean', 'Kids playing in sand']
        },
        {
          id: 3,
          type: 'memory_search',
          content: 'Searched for "family vacation memories"',
          response: 'Found 24 relevant items across photos and videos',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          contentType: 'search',
          sentiment: 'positive',
          resultsCount: 24,
          processingTime: 0.8,
          searchTerms: ['family', 'vacation', 'memories']
        },
        {
          id: 4,
          type: 'agent',
          content: 'Used calculator agent for complex math',
          response: 'Solved differential equation with step-by-step explanation',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
          contentType: 'calculation',
          sentiment: 'neutral',
          agentUsed: 'CalculatorAgent',
          complexity: 'high',
          processingTime: 2.1
        },
        {
          id: 5,
          type: 'media',
          content: 'Uploaded presentation video',
          response: 'Transcribed audio and extracted key frames',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
          contentType: 'video',
          sentiment: 'neutral',
          duration: 1800, // 30 minutes
          transcription: 'AI and machine learning trends for 2024...',
          keyFrames: 15
        }
      ];
      setLogs(mockLogs);
    } catch (error) {
      console.error('Failed to load memory logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMemoryStats = async () => {
    // Mock stats
    setStats({
      totalInteractions: 1247,
      avgResponseTime: 1.8,
      topAgents: ['WeatherAgent', 'CalculatorAgent', 'NewsAgent'],
      memorySize: '2.4 GB',
      searchAccuracy: 94.2
    });
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.content.toLowerCase().includes(query) ||
        log.response.toLowerCase().includes(query) ||
        (log.agentUsed && log.agentUsed.toLowerCase().includes(query))
      );
    }

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(log => log.type === filters.type);
    }

    // Content type filter
    if (filters.contentType !== 'all') {
      filtered = filtered.filter(log => log.contentType === filters.contentType);
    }

    // Date filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      
      if (filters.dateRange !== 'all') {
        filtered = filtered.filter(log => new Date(log.timestamp) >= cutoff);
      }
    }

    // Sentiment filter
    if (filters.sentiment !== 'all') {
      filtered = filtered.filter(log => log.sentiment === filters.sentiment);
    }

    setFilteredLogs(filtered);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'chat': return <ChatBubbleLeftIcon className="w-5 h-5 text-blue-500" />;
      case 'media': return <PhotoIcon className="w-5 h-5 text-green-500" />;
      case 'memory_search': return <MagnifyingGlassIcon className="w-5 h-5 text-pink-500" />;
      case 'agent': return <CpuChipIcon className="w-5 h-5 text-purple-500" />;
      default: return <DocumentIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'image': return <PhotoIcon className="w-4 h-4" />;
      case 'video': return <VideoCameraIcon className="w-4 h-4" />;
      case 'audio': return <SpeakerWaveIcon className="w-4 h-4" />;
      case 'conversation': return <ChatBubbleLeftIcon className="w-4 h-4" />;
      default: return <DocumentIcon className="w-4 h-4" />;
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <FaceSmileIcon className="w-4 h-4 text-green-500" />;
      case 'negative': return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      default: return <div className="w-4 h-4 bg-gray-400 rounded-full"></div>;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Type', 'Content', 'Response', 'Processing Time'].join(','),
      ...filteredLogs.map(log => [
        log.timestamp,
        log.type,
        `"${log.content}"`,
        `"${log.response}"`,
        log.processingTime || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memory_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilters({
      type: 'all',
      dateRange: 'all',
      contentType: 'all',
      sentiment: 'all'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Memory Logs</h2>
          <p className="text-gray-600">Track and analyze all your AI interactions and content processing</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FunnelIcon className="w-4 h-4 mr-2" />
            Filters
          </button>
          <button
            onClick={exportLogs}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.totalInteractions}</div>
          <div className="text-sm text-gray-600">Total Interactions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{stats.avgResponseTime}s</div>
          <div className="text-sm text-gray-600">Avg Response Time</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-purple-600">{stats.memorySize}</div>
          <div className="text-sm text-gray-600">Memory Size</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-pink-600">{stats.searchAccuracy}%</div>
          <div className="text-sm text-gray-600">Search Accuracy</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-600">{filteredLogs.length}</div>
          <div className="text-sm text-gray-600">Filtered Results</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        {/* Search Bar */}
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search memory logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Types</option>
                <option value="chat">Chat</option>
                <option value="media">Media</option>
                <option value="memory_search">Memory Search</option>
                <option value="agent">Agent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
              <select
                value={filters.contentType}
                onChange={(e) => setFilters({...filters, contentType: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Content</option>
                <option value="conversation">Conversation</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="search">Search</option>
                <option value="calculation">Calculation</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sentiment</label>
              <select
                value={filters.sentiment}
                onChange={(e) => setFilters({...filters, sentiment: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Sentiments</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </div>

            <div className="md:col-span-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Memory Logs List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Activity Timeline ({filteredLogs.length} items)
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredLogs.map((log) => (
            <div key={log.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start space-x-4">
                {/* Type Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getTypeIcon(log.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900">{log.content}</h4>
                      <div className="flex items-center space-x-1">
                        {getContentIcon(log.contentType)}
                        {getSentimentIcon(log.sentiment)}
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {formatTimeAgo(log.timestamp)}
                    </div>
                  </div>

                  <p className="text-sm text-gray-700 mb-2">{log.response}</p>

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                    {log.processingTime && (
                      <span>⚡ {log.processingTime}s</span>
                    )}
                    {log.agentUsed && (
                      <span>🤖 {log.agentUsed}</span>
                    )}
                    {log.mediaCount && (
                      <span>📁 {log.mediaCount} files</span>
                    )}
                    {log.resultsCount && (
                      <span>🔍 {log.resultsCount} results</span>
                    )}
                    {log.tokens && (
                      <span>💬 {log.tokens} tokens</span>
                    )}
                    <span className="capitalize">
                      📊 {log.contentType}
                    </span>
                  </div>

                  {/* Additional Details */}
                  {(log.extractedText || log.transcription || log.searchTerms) && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                      {log.extractedText && (
                        <div><strong>Text:</strong> {log.extractedText}</div>
                      )}
                      {log.transcription && (
                        <div><strong>Transcription:</strong> {log.transcription.substring(0, 100)}...</div>
                      )}
                      {log.searchTerms && (
                        <div><strong>Search terms:</strong> {log.searchTerms.join(', ')}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button className="p-1 text-gray-400 hover:text-gray-600">
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button className="p-1 text-gray-400 hover:text-red-600">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredLogs.length === 0 && (
          <div className="p-12 text-center">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No logs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryLogs;